// client/src/dnd/components/PixiLayer.js
import React, { useRef, useEffect, useState, useMemo } from "react";
import { useMap } from "react-leaflet";
import CombinedVisionOverlay from "./pixi_parts/CombinedVisionOverlay";
import SpellOverlay from "./pixi_parts/SpellOverlay";
import WeatherOverlay from "./pixi_parts/WeatherOverlay";
import { castPendingSpellAtTarget } from "./pixi_parts/SpellManager.js";
import { buildCollisionIndex } from "./pixi_parts/collisionIndex.js";

const GLOBAL_LIGHT_ZERO_DEGREE_SHADOW_METRES = 30;

function buildCollisionIgnoreIdsForOriginToken(token) {
  if (!token) return [];
  const rawId = token?.id != null ? String(token.id) : null;
  if (!rawId) return [];

  const rawType = String(token?.type || "player").toLowerCase();
  const tokenType = rawType === "npc" ? "npc" : "player";

  return [
    `character:${tokenType}:${rawId}`,
    rawId,
    Number.isFinite(Number(rawId)) ? Number(rawId) : null,
  ].filter(Boolean);
}

export default function PixiLayer(props) {
  const {
    imageSize,
    hardcodedMarker,
    playerTokens,
    proximityPlayerTokens,
    draggablePlayerTokens,
    renderedPlayerTokens,
    lightMarkers,
    sightMarkers,
    wallPolygons,
    tokens,
    fogOfWar,
    isMapOwnerOrFounder,
    gridConfig,
    mapId,
    overlayAssets,
    onHardcodedMove,
    onUpdateToken,
    onPersistTokenPosition,
    onOpenSpellMenu,
    onPlayerTokenClick,
  } = props;

  const map = useMap();
  const [pathingLightsById, setPathingLightsById] = useState({});
  const [, setFogAlpha] = useState(0);
  const [hcOverride, setHcOverride] = useState(null);
  const [playerOverrides, setPlayerOverrides] = useState({});
  const [draggingTarget, setDraggingTarget] = useState(null);
  const [mapViewVersion, setMapViewVersion] = useState(0);
  const draggingRef = useRef(false);
  const tokenContainerRef = useRef(null);
  const resizeIdleTimerRef = useRef(null);
  const dragStateRef = useRef(null);
  const dragMovedRef = useRef(false);
  const [freezeDuringResize, setFreezeDuringResize] = useState(false);
  const proximitySendTimerRef = useRef(null);
  const lastSentProximityKeyRef = useRef("");
  const ownedCollisionTokenIdsRef = useRef([]);

  function beginGlobalNoSelect() {
    if (typeof document === "undefined") return;
    try {
      document.documentElement.classList.add("no-select");
      if (document.body) document.body.classList.add("no-select");
    } catch {}
  }

  function endGlobalNoSelect() {
    if (typeof document === "undefined") return;
    try {
      document.documentElement.classList.remove("no-select");
      if (document.body) document.body.classList.remove("no-select");
    } catch {}
  }

  function clampToImage(x, y, size) {
    if (!size || !Array.isArray(size) || size.length < 2) return { x, y };
    const w = Number(size[0]) || 0;
    const h = Number(size[1]) || 0;
    return {
      x: Math.max(0, Math.min(w, Number(x))),
      y: Math.max(0, Math.min(h, Number(y))),
    };
  }

  function getPlayerRadiusImageUnits() {
    if (gridConfig && typeof gridConfig.cell_size === "number" && gridConfig.cell_size > 0) {
      return gridConfig.cell_size * 0.5;
    }
    return 18;
  }

  const normalizedPlayerTokens = useMemo(() => {
    return (Array.isArray(playerTokens) ? playerTokens : [])
      .map((t, idx) => {
        const id = t?.id != null ? String(t.id) : `player-${idx}`;
        const override = playerOverrides[id];
        const rawX = override && Number.isFinite(override.x) ? override.x : t?.x;
        const rawY = override && Number.isFinite(override.y) ? override.y : t?.y;
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
        const pos = clampToImage(rawX, rawY, imageSize);
        return {
          ...t,
          id,
          x: pos.x,
          y: pos.y,
          colour: t?.colour || "#3b82f6",
          label: t?.label || "Player",
        };
      })
      .filter(Boolean);
  }, [playerTokens, playerOverrides, imageSize]);

  const normalizedProximityPlayerTokens = useMemo(() => {
    const source = Array.isArray(proximityPlayerTokens) ? proximityPlayerTokens : (Array.isArray(playerTokens) ? playerTokens : []);
    return source
      .map((t, idx) => {
        const id = t?.id != null ? String(t.id) : `proximity-player-${idx}`;
        const override = playerOverrides[id];
        const rawX = override && Number.isFinite(override.x) ? override.x : t?.x;
        const rawY = override && Number.isFinite(override.y) ? override.y : t?.y;
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
        const pos = clampToImage(rawX, rawY, imageSize);
        return {
          ...t,
          id,
          x: pos.x,
          y: pos.y,
          colour: t?.colour || "#3b82f6",
          label: t?.label || "Player",
        };
      })
      .filter(Boolean);
  }, [proximityPlayerTokens, playerTokens, playerOverrides, imageSize]);

  const normalizedDraggablePlayerTokens = useMemo(() => {
    const source = Array.isArray(draggablePlayerTokens) ? draggablePlayerTokens : [];
    return source
      .map((t, idx) => {
        const id = t?.id != null ? String(t.id) : `draggable-player-${idx}`;
        const override = playerOverrides[id];
        const rawX = override && Number.isFinite(override.x) ? override.x : t?.x;
        const rawY = override && Number.isFinite(override.y) ? override.y : t?.y;
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
        const pos = clampToImage(rawX, rawY, imageSize);
        return {
          ...t,
          id,
          x: pos.x,
          y: pos.y,
          colour: t?.colour || "#3b82f6",
          label: t?.label || "Player",
        };
      })
      .filter(Boolean);
  }, [draggablePlayerTokens, playerOverrides, imageSize]);

  const normalizedRenderedPlayerTokens = useMemo(() => {
    const source = Array.isArray(renderedPlayerTokens) ? renderedPlayerTokens : (Array.isArray(playerTokens) ? playerTokens : []);
    return source
      .map((t, idx) => {
        const id = t?.id != null ? String(t.id) : `rendered-player-${idx}`;
        const override = playerOverrides[id];
        const rawX = override && Number.isFinite(override.x) ? override.x : t?.x;
        const rawY = override && Number.isFinite(override.y) ? override.y : t?.y;
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
        const pos = clampToImage(rawX, rawY, imageSize);
        return {
          ...t,
          id,
          x: pos.x,
          y: pos.y,
          colour: t?.colour || "#3b82f6",
          label: t?.label || "Player",
        };
      })
      .filter(Boolean);
  }, [renderedPlayerTokens, playerTokens, playerOverrides, imageSize]);

  const normalizedSightMarkers = useMemo(() => {
    return (Array.isArray(sightMarkers) ? sightMarkers : [])
      .map((t, idx) => {
        const id = t?.id != null ? String(t.id) : `sight-${idx}`;
        const override = playerOverrides[id];
        const rawX = override && Number.isFinite(override.x) ? override.x : t?.x;
        const rawY = override && Number.isFinite(override.y) ? override.y : t?.y;
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
        const pos = clampToImage(rawX, rawY, imageSize);
        return {
          ...t,
          id,
          x: pos.x,
          y: pos.y,
        };
      })
      .filter(Boolean);
  }, [sightMarkers, playerOverrides, imageSize]);

  const proximityBasePayload = useMemo(() => {
    return {
      mapId: mapId != null ? Number(mapId) : null,
      metreInPixels: Number(gridConfig?.metre_in_pixels || 0),
      tokens: normalizedProximityPlayerTokens
        .map((t) => {
          const linkedUserId = Number(t?.controlled_user_id || 0);
          const x = Number(t?.x);
          const y = Number(t?.y);
          if (!linkedUserId || !Number.isFinite(x) || !Number.isFinite(y)) return null;
          return {
            id: t?.id != null ? String(t.id) : "",
            userId: linkedUserId,
            x,
            y,
          };
        })
        .filter(Boolean),
    };
  }, [mapId, gridConfig?.metre_in_pixels, normalizedProximityPlayerTokens]);

  const currentHCPos = useMemo(() => {
    if (hcOverride && Number.isFinite(hcOverride.x) && Number.isFinite(hcOverride.y)) {
      return clampToImage(hcOverride.x, hcOverride.y, imageSize);
    }
    const hx =
      Number.isFinite(hardcodedMarker?.x)
        ? hardcodedMarker.x
        : Number.isFinite(hardcodedMarker?.lng)
          ? hardcodedMarker.lng
          : undefined;
    const hy =
      Number.isFinite(hardcodedMarker?.y)
        ? hardcodedMarker.y
        : Number.isFinite(hardcodedMarker?.lat)
          ? hardcodedMarker.lat
          : undefined;
    if (!Number.isFinite(hx) || !Number.isFinite(hy)) return null;
    return clampToImage(hx, hy, imageSize);
  }, [hcOverride, hardcodedMarker, imageSize]);

  const collisionIndex = useMemo(() => {
    return buildCollisionIndex({
      imageSize,
      wallPolygons,
      renderedTokens: normalizedRenderedPlayerTokens,
      gridConfig,
      fallbackTokenRadius: getPlayerRadiusImageUnits(),
    });
  }, [
    imageSize,
    wallPolygons,
    normalizedRenderedPlayerTokens,
    gridConfig?.metre_in_pixels,
    gridConfig?.meter_in_pixels,
    gridConfig?.cell_size,
    gridConfig?.square_length,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (currentHCPos && Number.isFinite(currentHCPos.x) && Number.isFinite(currentHCPos.y)) {
        window.__PA_SPELL_ORIGIN = { x: currentHCPos.x, y: currentHCPos.y };
      } else if (window.__PA_SPELL_ORIGIN) {
        delete window.__PA_SPELL_ORIGIN;
      }
    } catch {}
  }, [currentHCPos]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (wallPolygons) {
        window.__PA_WALLS = wallPolygons;
      } else if (window.__PA_WALLS) {
        delete window.__PA_WALLS;
      }
    } catch {}
  }, [wallPolygons]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.__PA_COLLISION_INDEX = collisionIndex || null;
      window.__PA_WALL_SEGMENTS_IMAGE = Array.isArray(collisionIndex?.walls?.segments)
        ? collisionIndex.walls.segments
        : [];
      window.__PA_COLLISION_GRID_VERSION = Number(collisionIndex?.version || 0);
    } catch {}

    return () => {
      try {
        delete window.__PA_COLLISION_INDEX;
        delete window.__PA_WALL_SEGMENTS_IMAGE;
        delete window.__PA_COLLISION_GRID_VERSION;
      } catch {}
    };
  }, [collisionIndex]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const prevIds = Array.isArray(ownedCollisionTokenIdsRef.current)
      ? ownedCollisionTokenIdsRef.current.slice()
      : [];

    const metreInPixels = Number(gridConfig?.metre_in_pixels || 0);
    const fallbackRadius = getPlayerRadiusImageUnits();

    const nextTokens = normalizedRenderedPlayerTokens
      .map((token, idx) => {
        const x = Number(token?.x);
        const y = Number(token?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

        const rawId = token?.id != null ? String(token.id) : `rendered-${idx}`;
        const tokenType = String(token?.type || "player").toLowerCase() === "npc" ? "npc" : "player";
        const collisionId = `character:${tokenType}:${rawId}`;

        return {
          id: collisionId,
          x,
          y,
          radius: fallbackRadius,
          metre_in_pixels: metreInPixels > 0 ? metreInPixels : undefined,
          token_type: tokenType,
          source_id: rawId,
        };
      })
      .filter(Boolean);

    const nextIds = nextTokens.map((token) => token.id);

    try {
      if (!window.__PA_COLLISION_TOKENS_IMAGE || typeof window.__PA_COLLISION_TOKENS_IMAGE !== "object") {
        window.__PA_COLLISION_TOKENS_IMAGE = {};
      }

      prevIds.forEach((id) => {
        if (!nextIds.includes(id)) {
          delete window.__PA_COLLISION_TOKENS_IMAGE[id];
        }
      });

      nextTokens.forEach((token) => {
        window.__PA_COLLISION_TOKENS_IMAGE[token.id] = token;
      });
    } catch {}

    try {
      const existing = Array.isArray(window.__PA_TOKENS) ? window.__PA_TOKENS : [];
      const filtered = existing.filter((token) => token && !prevIds.includes(token.id));
      window.__PA_TOKENS = [...filtered, ...nextTokens];
    } catch {}

    ownedCollisionTokenIdsRef.current = nextIds;

    return () => {
      try {
        const ownedIds = Array.isArray(ownedCollisionTokenIdsRef.current)
          ? ownedCollisionTokenIdsRef.current.slice()
          : [];

        if (window.__PA_COLLISION_TOKENS_IMAGE && typeof window.__PA_COLLISION_TOKENS_IMAGE === "object") {
          ownedIds.forEach((id) => {
            delete window.__PA_COLLISION_TOKENS_IMAGE[id];
          });
        }

        if (Array.isArray(window.__PA_TOKENS)) {
          window.__PA_TOKENS = window.__PA_TOKENS.filter((token) => token && !ownedIds.includes(token.id));
        }
      } catch {}
    };
  }, [normalizedRenderedPlayerTokens, gridConfig?.metre_in_pixels, gridConfig?.cell_size]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (ev) => {
      const d = ev?.detail || {};
      const rawId = d.id;
      const id = rawId != null ? String(rawId) : "__pathing-unknown__";
      const light = d.light || null;

      setPathingLightsById((prev) => {
        const next = { ...prev };
        if (!light) {
          delete next[id];
        } else {
          next[id] = {
            id,
            ...light,
          };
        }
        return next;
      });
    };

    window.addEventListener("pa-path-light-update", handler);
    return () => {
      window.removeEventListener("pa-path-light-update", handler);
    };
  }, []);

  useEffect(() => {
    if (!map) return;

    function handleClick(e) {
      if (!e || !e.latlng) return;
      const { lat, lng } = e.latlng;
      const imgX = lng;
      const imgY = lat;

      try {
        const target = { x: imgX, y: imgY };
        const originContext =
          typeof window !== "undefined" && window.__PA_SPELL_ORIGIN_CONTEXT
            ? window.__PA_SPELL_ORIGIN_CONTEXT
            : null;

        castPendingSpellAtTarget(target, {
          gridConfig,
          wallPolygons,
          ignoreCollisionIds: Array.isArray(originContext?.ignoreCollisionIds) ? originContext.ignoreCollisionIds : [],
          sourceTokenId: originContext?.sourceTokenId != null ? String(originContext.sourceTokenId) : null,
          sourceTokenType: originContext?.sourceTokenType || null,
        });
      } catch {}
    }

    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
    };
  }, [map, gridConfig, wallPolygons]);

  useEffect(() => {
    if (!map) return;

    const updateAll = () => {
      setMapViewVersion((v) => v + 1);
    };

    updateAll();
    map.on("zoom viewreset resize move moveend zoomend", updateAll);

    return () => {
      map.off("zoom viewreset resize move moveend zoomend", updateAll);
    };
  }, [map]);

  useEffect(() => {
    if (!map || typeof ResizeObserver === "undefined") return;

    const container = map.getContainer();
    if (!container) return;

    let first = true;

    const handleResize = () => {
      if (first) {
        first = false;
        return;
      }
      setFreezeDuringResize(true);
      if (resizeIdleTimerRef.current) {
        clearTimeout(resizeIdleTimerRef.current);
      }
      resizeIdleTimerRef.current = setTimeout(() => {
        setFreezeDuringResize(false);
      }, 140);
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(container);

    return () => {
      ro.disconnect();
      if (resizeIdleTimerRef.current) {
        clearTimeout(resizeIdleTimerRef.current);
        resizeIdleTimerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const payload = {
      ...proximityBasePayload,
      updatedAt: Date.now(),
    };

    try {
      window.__PA_VTT_VOICE_PROXIMITY = payload;
    } catch {}

    try {
      window.dispatchEvent(new CustomEvent("pa-vtt-proximity-changed", { detail: payload }));
    } catch {}

    return () => {
      try {
        const cur = window.__PA_VTT_VOICE_PROXIMITY;
        if (cur && Number(cur.mapId) === Number(mapId)) {
          delete window.__PA_VTT_VOICE_PROXIMITY;
        }
      } catch {}
      try {
        window.dispatchEvent(new CustomEvent("pa-vtt-proximity-changed", {
          detail: {
            mapId: mapId != null ? Number(mapId) : null,
            metreInPixels: 0,
            updatedAt: Date.now(),
            tokens: [],
          },
        }));
      } catch {}
    };
  }, [mapId, proximityBasePayload]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!proximityBasePayload.mapId) return;

    const payloadForSend = {
      ...proximityBasePayload,
      updatedAt: Date.now(),
    };

    const sendKey = JSON.stringify({
      mapId: payloadForSend.mapId,
      metreInPixels: payloadForSend.metreInPixels,
      tokens: payloadForSend.tokens,
    });

    if (sendKey === lastSentProximityKeyRef.current) return;

    if (proximitySendTimerRef.current) {
      clearTimeout(proximitySendTimerRef.current);
      proximitySendTimerRef.current = null;
    }

    proximitySendTimerRef.current = setTimeout(() => {
      proximitySendTimerRef.current = null;
      lastSentProximityKeyRef.current = sendKey;

      fetch("/api/coms/voice/proximity/update", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payloadForSend),
      }).catch(() => {});
    }, draggingRef.current ? 90 : 0);

    return () => {
      if (proximitySendTimerRef.current) {
        clearTimeout(proximitySendTimerRef.current);
        proximitySendTimerRef.current = null;
      }
    };
  }, [proximityBasePayload]);

  const mergedTokens = useMemo(() => {
    return Array.isArray(tokens) ? tokens : [];
  }, [tokens]);

  const mergedSightMarkers = useMemo(() => {
    const base = normalizedSightMarkers.map((t) => ({
      id: t.id,
      x: t.x,
      y: t.y,
    }));

    if (!currentHCPos) return base;

    const hc = {
      id: `hc-sight-${mapId || "unknown"}`,
      x: currentHCPos.x,
      y: currentHCPos.y,
    };

    return [...base, hc];
  }, [normalizedSightMarkers, currentHCPos, mapId]);

  const mergedLightMarkers = useMemo(() => {
    const base = Array.isArray(lightMarkers) ? lightMarkers : [];
    const pathingLights = Object.values(pathingLightsById);

    let result = [...base, ...pathingLights];

    if (!currentHCPos || !hardcodedMarker) return result;

    const id = `hardcoded-player-light-${mapId || "unknown"}`;
    const reach = Number.isFinite(hardcodedMarker.reach) ? hardcodedMarker.reach : 1;
    const hcLight = {
      id,
      x: currentHCPos.x,
      y: currentHCPos.y,
      type: "light",
      light_type: "darksight",
      reach,
      reach_units: "metres",
      intensity: 0.4,
      colour: hardcodedMarker.colour || "#3b82f6",
      emits_light: 3,
      flicker: 0,
      label: hardcodedMarker.label || "Player Darksight",
    };
    const withoutOld = result.filter((l) => l?.id !== id);
    return [...withoutOld, hcLight];
  }, [lightMarkers, pathingLightsById, currentHCPos, hardcodedMarker, mapId]);

  const overlayFogAssets = useMemo(() => {
    if (!Array.isArray(overlayAssets)) return [];
    return overlayAssets.filter((a) => {
      const t = String(a?.asset_type || a?.type || "").toLowerCase();
      return t === "overlay";
    });
  }, [overlayAssets]);

  function buildTokenScreenStyle(token) {
    if (!map || !token) {
      return {
        display: "none",
      };
    }

    const center = map.latLngToContainerPoint([token.y, token.x]);
    const edge = map.latLngToContainerPoint([token.y, token.x + getPlayerRadiusImageUnits()]);
    const radiusPx = Math.max(8, Math.abs(edge.x - center.x));
    const diameter = radiusPx * 2;

    return {
      position: "absolute",
      left: `${center.x - radiusPx}px`,
      top: `${center.y - radiusPx}px`,
      width: `${diameter}px`,
      height: `${diameter}px`,
      borderRadius: "50%",
      background: token.colour || "#3b82f6",
      border: "2.5px solid #23242a",
      boxSizing: "border-box",
      pointerEvents: "none",
      transform: "translateZ(0)",
      boxShadow: "0 2px 8px #0008",
    };
  }

  const hardcodedPlayerScreenStyle = useMemo(() => {
    if (!currentHCPos) return { display: "none" };
    return buildTokenScreenStyle({
      x: currentHCPos.x,
      y: currentHCPos.y,
      colour: hardcodedMarker?.colour || "#3b82f6",
    });
  }, [currentHCPos, hardcodedMarker, map, gridConfig, mapViewVersion]);

  const visibleVisionPlayerIdSet = useMemo(() => {
    return new Set(normalizedPlayerTokens.map((token) => token.id));
  }, [normalizedPlayerTokens]);

  const otherPlayerTokenScreenItems = useMemo(() => {
    return normalizedRenderedPlayerTokens
      .filter((token) => !visibleVisionPlayerIdSet.has(token.id))
      .map((token) => ({
        token,
        style: buildTokenScreenStyle(token),
      }));
  }, [normalizedRenderedPlayerTokens, visibleVisionPlayerIdSet, map, gridConfig, mapViewVersion]);

  const playerTokenScreenItems = useMemo(() => {
    return normalizedPlayerTokens.map((token) => ({
      token,
      style: buildTokenScreenStyle(token),
    }));
  }, [normalizedPlayerTokens, map, gridConfig, mapViewVersion]);

  useEffect(() => {
    if (!map || !imageSize) return;

    const container = map.getContainer();
    if (!container) return;

    function imageToScreen(x, y) {
      const p = map.latLngToContainerPoint([y, x]);
      return { x: p.x, y: p.y };
    }

    function screenToImage(cx, cy) {
      const ll = map.containerPointToLatLng([cx, cy]);
      return { x: ll.lng, y: ll.lat };
    }

    function dist2(a, b) {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      return dx * dx + dy * dy;
    }

    let lastCursor = "";
    const radiusImg = getPlayerRadiusImageUnits();

    const getRadiusScreen = (token) => {
      if (!token) return 0;
      const center = imageToScreen(token.x, token.y);
      const edge = imageToScreen(token.x + radiusImg, token.y);
      return Math.max(8, Math.abs(edge.x - center.x));
    };

    const getAllTargets = () => {
      const result = [];

      normalizedDraggablePlayerTokens.forEach((token) => {
        result.push({
          kind: "player",
          id: token.id,
          token,
        });
      });

      normalizedRenderedPlayerTokens.forEach((token) => {
        if (result.some((item) => item.kind === "player" && item.id === token.id)) return;
        result.push({
          kind: "player-info",
          id: token.id,
          token,
        });
      });

      if (currentHCPos) {
        result.push({
          kind: "hardcoded",
          id: "__hardcoded__",
          token: {
            x: currentHCPos.x,
            y: currentHCPos.y,
            colour: hardcodedMarker?.colour || "#3b82f6",
            label: hardcodedMarker?.label || "Player",
            id: hardcodedMarker?.id != null ? String(hardcodedMarker.id) : "__hardcoded__",
            type: "player",
          },
        });
      }

      return result;
    };

    const pickTargetAtPoint = (pt) => {
      const targets = getAllTargets();

      for (let i = targets.length - 1; i >= 0; i -= 1) {
        const candidate = targets[i];
        const tokenScreen = imageToScreen(candidate.token.x, candidate.token.y);
        const radiusScreen = getRadiusScreen(candidate.token);

        if (dist2(pt, tokenScreen) <= radiusScreen * radiusScreen) {
          return candidate;
        }
      }

      return null;
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;

      const rect = container.getBoundingClientRect();
      const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const target = pickTargetAtPoint(pt);

      if (!target) return;

      if (target.kind === "player-info") {
        if (typeof onPlayerTokenClick === "function") {
          onPlayerTokenClick(target.token, { clientX: e.clientX, clientY: e.clientY, button: 0 });
        }
        return;
      }

      if (typeof e.preventDefault === "function") e.preventDefault();
      if (typeof e.stopPropagation === "function") e.stopPropagation();

      draggingRef.current = true;
      dragMovedRef.current = false;
      dragStateRef.current = {
        ...target,
        startClientX: e.clientX,
        startClientY: e.clientY,
      };
      setDraggingTarget({ kind: target.kind, id: target.id });
      lastCursor = container.style.cursor;
      container.style.cursor = "grabbing";
      beginGlobalNoSelect();
      map.dragging?.disable();
    };

    const onMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };

      if (draggingRef.current && dragStateRef.current) {
        const dragDx = Math.abs(e.clientX - dragStateRef.current.startClientX);
        const dragDy = Math.abs(e.clientY - dragStateRef.current.startClientY);
        if (dragDx > 4 || dragDy > 4) {
          dragMovedRef.current = true;
        }

        const img = screenToImage(pt.x, pt.y);
        const clamped = clampToImage(img.x, img.y, imageSize);
        const target = dragStateRef.current;

        if (target.kind === "hardcoded") {
          setHcOverride(clamped);
          if (typeof onHardcodedMove === "function") {
            onHardcodedMove(clamped);
          }
        } else if (target.kind === "player") {
          setPlayerOverrides((prev) => ({
            ...prev,
            [target.id]: clamped,
          }));
          if (typeof onUpdateToken === "function") {
            const original = normalizedDraggablePlayerTokens.find((t) => t.id === target.id);
            if (original) {
              onUpdateToken({
                ...original,
                x: clamped.x,
                y: clamped.y,
              });
            }
          }
        }

        return;
      }

      const hoverTarget = pickTargetAtPoint(pt);
      if (hoverTarget) {
        container.style.cursor = "grab";
      } else if (container.style.cursor === "grab") {
        container.style.cursor = "";
      }
    };

    const endDrag = (e) => {
      if (!draggingRef.current) return;

      const target = dragStateRef.current;
      const wasDrag = !!dragMovedRef.current;

      if (target?.kind === "player" && target?.id) {
        const finalPos = playerOverrides[target.id];
        const original = normalizedDraggablePlayerTokens.find((t) => t.id === target.id);

        if (finalPos && original && typeof onPersistTokenPosition === "function" && wasDrag) {
          onPersistTokenPosition({
            ...original,
            x: finalPos.x,
            y: finalPos.y,
          });
        }

        if (!wasDrag && original && typeof onPlayerTokenClick === "function") {
          onPlayerTokenClick(original, {
            clientX: e?.clientX,
            clientY: e?.clientY,
            button: 0,
          });
        }
      }

      draggingRef.current = false;
      dragMovedRef.current = false;
      dragStateRef.current = null;
      setDraggingTarget(null);
      endGlobalNoSelect();
      container.style.cursor = lastCursor || "";
      map.dragging?.enable();
    };

    const onContextMenu = (e) => {
      if (typeof onOpenSpellMenu !== "function") return;
      if (typeof e.preventDefault === "function") e.preventDefault();
      if (typeof e.stopPropagation === "function") e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const target = pickTargetAtPoint(pt);

      const origin =
        target && target.token && Number.isFinite(target.token.x) && Number.isFinite(target.token.y)
          ? { x: target.token.x, y: target.token.y }
          : null;

      const ignoreCollisionIds =
        target && target.token
          ? buildCollisionIgnoreIdsForOriginToken(target.token)
          : [];

      try {
        if (origin) {
          window.__PA_SPELL_ORIGIN = origin;
          window.__PA_SPELL_ORIGIN_CONTEXT = {
            origin,
            ignoreCollisionIds,
            sourceTokenId: target?.token?.id != null ? String(target.token.id) : null,
            sourceTokenType: target?.token?.type ? String(target.token.type).toLowerCase() : null,
          };
        } else {
          delete window.__PA_SPELL_ORIGIN_CONTEXT;
        }
      } catch {}

      onOpenSpellMenu({
        x: e.clientX,
        y: e.clientY,
        origin,
        ignoreCollisionIds,
        sourceTokenId: target?.token?.id != null ? String(target.token.id) : null,
        sourceTokenType: target?.token?.type ? String(target.token.type).toLowerCase() : null,
      });
    };

    const onTouchStart = (ev) => {
      if (!ev.touches?.length) return;
      const t = ev.touches[0];
      onMouseDown({
        button: 0,
        clientX: t.clientX,
        clientY: t.clientY,
        preventDefault: () => {},
        stopPropagation: () => {},
      });
    };

    const onTouchMove = (ev) => {
      if (!ev.touches?.length) return;
      const t = ev.touches[0];
      onMouseMove({ clientX: t.clientX, clientY: t.clientY });
    };

    container.addEventListener("mousedown", onMouseDown);
    container.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endDrag);
    container.addEventListener("mouseleave", endDrag);
    container.addEventListener("contextmenu", onContextMenu);
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", endDrag);

    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      container.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endDrag);
      container.removeEventListener("mouseleave", endDrag);
      container.removeEventListener("contextmenu", onContextMenu);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endDrag);
      endGlobalNoSelect();
    };
  }, [
    map,
    imageSize,
    currentHCPos,
    normalizedDraggablePlayerTokens,
    normalizedRenderedPlayerTokens,
    playerOverrides,
    hardcodedMarker,
    gridConfig,
    onHardcodedMove,
    onUpdateToken,
    onPersistTokenPosition,
    onOpenSpellMenu,
    onPlayerTokenClick,
    mapViewVersion,
  ]);

  const fogOn = isMapOwnerOrFounder ? !!fogOfWar : true;

  return (
    <>
      <WeatherOverlay imageSize={imageSize} gridConfig={gridConfig} />
      <SpellOverlay imageSize={imageSize} gridConfig={gridConfig} />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 500,
          pointerEvents: "none",
          backgroundColor: "transparent",
        }}
      >
        {otherPlayerTokenScreenItems.map(({ token, style }) => (
          <div
            key={`other-${token.id}`}
            style={style}
          />
        ))}
      </div>

      {fogOn && (
        <CombinedVisionOverlay
          imageSize={imageSize}
          fogEnabled={true}
          wallPolygons={wallPolygons}
          tokens={mergedTokens}
          sightMarkers={mergedSightMarkers}
          lightMarkers={mergedLightMarkers}
          gridConfig={gridConfig || {}}
          coordTransformer={null}
          mapId={mapId}
          zIndex={700}
          overlayAssets={overlayFogAssets}
          freezeDuringResize={freezeDuringResize}
          zeroDegreeShadowMetres={GLOBAL_LIGHT_ZERO_DEGREE_SHADOW_METRES}
          onFogAlphaChange={setFogAlpha}
        />
      )}

      <div
        ref={tokenContainerRef}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 800,
        }}
      >
        {playerTokenScreenItems.map(({ token, style }) => (
          <div
            key={token.id}
            style={{
              ...style,
              opacity:
                draggingTarget &&
                draggingTarget.kind === "player" &&
                draggingTarget.id === token.id
                  ? 0.75
                  : 1,
            }}
          />
        ))}

        {currentHCPos && (
          <div
            style={{
              ...hardcodedPlayerScreenStyle,
              opacity:
                draggingTarget &&
                draggingTarget.kind === "hardcoded" &&
                draggingTarget.id === "__hardcoded__"
                  ? 0.75
                  : 1,
            }}
          />
        )}
      </div>
    </>
  );
}